import React, { useState } from 'react';
import { Modal as SparkModal, SegmentedControl, SparkModalPortal, TagColored } from '@spark';
import { EXAMPLE_PLACEHOLDER, buildFinalPromptSegments } from '../utils/playgroundFields';

export const FINAL_PROMPT_VIEWS = [
    { id: 'template', label: 'Template' },
    { id: 'as-sent', label: 'As sent' },
];

export const FINAL_PROMPT_SUBTITLES = {
    template: 'Your prompt, with field names.',
    'as-sent': 'Your prompt, with real values - this is what the model gets.',
};

/**
 * Mirror `combinePromptForDispatch`: the system and user prompts are sent as
 * one string, and `{{example}}` is appended when neither mentions it. Shown
 * per section so the modal matches the Model Configuration layout.
 */
export const finalPromptSections = ({ system, user } = {}) => {
    const systemText = (system || '').toString().trim();
    let userText = (user || '').toString().trim();
    const hasExample = systemText.includes(EXAMPLE_PLACEHOLDER) || userText.includes(EXAMPLE_PLACEHOLDER);
    if (!hasExample) userText = userText ? `${userText}\n${EXAMPLE_PLACEHOLDER}` : EXAMPLE_PLACEHOLDER;
    const sections = [];
    if (systemText) sections.push({ id: 'system', caption: 'SYSTEM PROMPT', text: systemText });
    sections.push({ id: 'user', caption: 'USER PROMPT', text: userText });
    return sections;
};

const PromptText = ({ text, view, exampleValue, fieldValues, knownFields }) => {
    const segments = buildFinalPromptSegments(text, { exampleValue, fieldValues, knownFields });
    return (
        <div className="pg-final-prompt__text">
            {segments.map((segment, idx) => {
                if (segment.type === 'text') {
                    // eslint-disable-next-line react/no-array-index-key
                    return <React.Fragment key={idx}>{segment.value}</React.Fragment>;
                }
                return view === 'template' ? (
                    <TagColored
                        // eslint-disable-next-line react/no-array-index-key
                        key={idx}
                        variant="blue"
                        label={`{{${segment.name}}}`}
                        className="pg-final-prompt__chip"
                    />
                ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <strong key={idx} className="pg-final-prompt__value" data-field={segment.name}>
                        {segment.value}
                    </strong>
                );
            })}
        </div>
    );
};

const FinalPromptBody = ({ modelName, prompts, exampleValue, fieldValues, knownFields, onClose }) => {
    const [view, setView] = useState('template');
    const sections = finalPromptSections(prompts || {});

    return (
        <SparkModal
            className="pg-final-prompt-modal fs-unmask"
            title={`Final prompt · ${modelName}`}
            subtitle={FINAL_PROMPT_SUBTITLES[view]}
            showSubtitle
            showScrollBar
            fixedHeight={false}
            showFooter={false}
            onCloseClick={onClose}
        >
            <div className="pg-final-prompt">
                <SegmentedControl
                    items={FINAL_PROMPT_VIEWS}
                    activeId={view}
                    onChange={setView}
                    size="small"
                    style="outlined"
                    className="pg-final-prompt__views"
                />
                {sections.map((section) => (
                    <div
                        className="pg-final-prompt__section"
                        key={section.id}
                        data-section={section.id}
                    >
                        <span className="pg-final-prompt__caption">{section.caption}</span>
                        <PromptText
                            text={section.text}
                            view={view}
                            exampleValue={exampleValue}
                            fieldValues={fieldValues}
                            knownFields={knownFields}
                        />
                    </div>
                ))}
            </div>
        </SparkModal>
    );
};

/**
 * "Final prompt · <model>" (Figma 2137:83246 / 2137:84960): the prompt a
 * model column sends for one example. Template shows field names as chips
 * (the example text inline); As sent shows the substituted values in bold.
 * Unknown `{{name}}` tokens stay as plain text in both views, like the
 * backend sends them.
 */
const FinalPromptModal = ({
    open,
    modelName,
    prompts,
    exampleValue = '',
    fieldValues = {},
    knownFields = [],
    onClose,
}) => (
    <SparkModalPortal open={open} onClose={onClose} size="slot">
        {open && (
            <FinalPromptBody
                modelName={modelName}
                prompts={prompts}
                exampleValue={exampleValue}
                fieldValues={fieldValues}
                knownFields={knownFields}
                onClose={onClose}
            />
        )}
    </SparkModalPortal>
);

export default FinalPromptModal;
